import type {
  StackedBarGraphDataPoint,
  StackedBarGraphSeries,
} from "@/nzds/graphs/stacked-bar-graph/stacked-bar-graph.types"
import {
  sumDecimalStrings,
  toDecimalAmountString,
} from "@/utils/sum-decimal-strings"

import { monthKeysBetween, subtractYearFromMonthKey } from "./site-bills-range"

export type SiteBillsChartMeasure = "cost" | "usage"

export interface SiteBillsChartBill {
  cost: string | null
  currency: string | null
  endDate: string
  id: string
  meterCode: string
  meterId: number
  startDate: string
  usage: string | null
  usageUnit: string | null
}

export interface SiteBillsChartPeriod {
  endDate: string
  id: string
  startDate: string
}

export interface SiteBillsChartAmount {
  amount: number
  label: string
}

export interface SiteBillsYearOverYearChange {
  kind: "change"
  /** Absolute size of (this year − last year) / last year. */
  ratio: number
  thisYearIsGreater: boolean
}

export type SiteBillsYearOverYear =
  | SiteBillsYearOverYearChange
  | { kind: "unavailable" }

export interface SiteBillsMonthMeterDetails {
  costs: SiteBillsChartAmount[]
  meterCode: string
  meterId: string
  periods: SiteBillsChartPeriod[]
  usages: SiteBillsChartAmount[]
  yearOverYear: {
    cost: SiteBillsYearOverYear
    usage: SiteBillsYearOverYear
  }
}

export interface SiteBillsMonthlyChartModel {
  data: StackedBarGraphDataPoint[]
  detailsByMonth: Record<string, Record<string, SiteBillsMonthMeterDetails>>
  series: StackedBarGraphSeries[]
  /** Shared unit or currency of every drawn segment. Absent when they differ. */
  yAxisLabel: string | null
}

interface AmountBucket {
  amount: string
  label: string
}

interface MutableMeterMonth {
  costs: Map<string, AmountBucket>
  meterCode: string
  periods: SiteBillsChartPeriod[]
  usages: Map<string, AmountBucket>
}

/** Latest start first. A shared start puts the later end first. */
const comparePeriodsByRecency = (
  left: SiteBillsChartPeriod,
  right: SiteBillsChartPeriod
): number => {
  const byStart = right.startDate.localeCompare(left.startDate)
  if (byStart !== 0) {
    return byStart
  }

  return right.endDate.localeCompare(left.endDate)
}

const addAmount = (
  buckets: Map<string, AmountBucket>,
  key: string,
  label: string,
  amount: string
): void => {
  const current = buckets.get(key)
  if (current) {
    current.amount = sumDecimalStrings([current.amount, amount])
    return
  }

  buckets.set(key, { amount, label })
}

const toSortedAmounts = (
  buckets: Map<string, AmountBucket>
): SiteBillsChartAmount[] =>
  [...buckets.values()]
    .sort((left, right) => left.label.localeCompare(right.label))
    .map((bucket) => ({
      amount: Number(bucket.amount),
      label: bucket.label,
    }))

const unitKey = (unit: string): string => unit.trim().toLowerCase()

const currencyKey = (currency: string): string => currency.trim().toUpperCase()

const isWithinRange = (
  startDate: string,
  rangeStart: string,
  rangeEnd: string
): boolean => {
  const date = startDate.slice(0, 10)
  return date >= rangeStart && date <= rangeEnd
}

const monthKeyForDate = (startDate: string): string | null => {
  const date = startDate.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return null
  }

  return date.slice(0, 7)
}

const amountsForMeasure = (
  meter: SiteBillsMonthMeterDetails,
  measure: SiteBillsChartMeasure
): readonly SiteBillsChartAmount[] => {
  switch (measure) {
    case "usage":
      return meter.usages
    case "cost":
      return meter.costs
    default: {
      const unreachable: never = measure
      return unreachable
    }
  }
}

const normalizeForMeasure = (
  label: string,
  measure: SiteBillsChartMeasure
): string => {
  switch (measure) {
    case "usage":
      return unitKey(label)
    case "cost":
      return currencyKey(label)
    default: {
      const unreachable: never = measure
      return unreachable
    }
  }
}

/**
 * A stacked bar's height is one total. Draw it only when every meter in the
 * month has a single unit (usage) or a single currency (cost), and those
 * values match. Mixed units or currencies draw no bar.
 */
const monthSharesOneMeasure = (
  meters: readonly SiteBillsMonthMeterDetails[],
  measure: SiteBillsChartMeasure
): boolean => {
  if (meters.length === 0) {
    return false
  }

  const keys = meters.map((meter) => {
    const amounts = amountsForMeasure(meter, measure)
    const label = amounts.length === 1 ? amounts[0]?.label : null
    if (label == null || label.trim() === "") {
      return null
    }

    return normalizeForMeasure(label, measure)
  })

  if (keys.some((key) => key == null)) {
    return false
  }

  return new Set(keys).size === 1
}

const toMeterDetails = (
  meterId: string,
  meter: MutableMeterMonth
): Omit<SiteBillsMonthMeterDetails, "yearOverYear"> => ({
  costs: toSortedAmounts(meter.costs),
  meterCode: meter.meterCode,
  meterId,
  periods: [...meter.periods].sort(comparePeriodsByRecency),
  usages: toSortedAmounts(meter.usages),
})

interface ComparableTotal {
  amount: number
  label: string
}

/**
 * One finite amount with a label. A blank label is not a unit or a currency.
 */
const toComparableTotal = (
  amounts: readonly SiteBillsChartAmount[],
  measure: SiteBillsChartMeasure
): ComparableTotal | null => {
  if (amounts.length !== 1) {
    return null
  }

  const total = amounts[0]
  if (total == null || !Number.isFinite(total.amount)) {
    return null
  }

  const label = normalizeForMeasure(total.label, measure)
  if (label === "") {
    return null
  }

  return { amount: total.amount, label }
}

/**
 * Both years need one total, and those totals need the same normalized
 * label. Amounts are not converted. Last year at zero has no percent. The
 * ratio is the size of the change; the flag says which total is greater,
 * including when last year is negative.
 */
const compareYearOverYearTotals = (
  thisYear: ComparableTotal | null,
  lastYear: ComparableTotal | null
): SiteBillsYearOverYear => {
  if (thisYear == null || lastYear == null) {
    return { kind: "unavailable" }
  }

  if (thisYear.label !== lastYear.label || lastYear.amount === 0) {
    return { kind: "unavailable" }
  }

  return {
    kind: "change",
    ratio: Math.abs((thisYear.amount - lastYear.amount) / lastYear.amount),
    thisYearIsGreater: thisYear.amount > lastYear.amount,
  }
}

const yearOverYearForMeter = (
  monthKey: string,
  meter: Omit<SiteBillsMonthMeterDetails, "yearOverYear">,
  monthsByStart: ReadonlyMap<string, ReadonlyMap<string, MutableMeterMonth>>
): SiteBillsMonthMeterDetails["yearOverYear"] => {
  const priorMonth = subtractYearFromMonthKey(monthKey)
  const prior = priorMonth
    ? monthsByStart.get(priorMonth)?.get(meter.meterId)
    : undefined
  const priorDetails = prior ? toMeterDetails(meter.meterId, prior) : null

  return {
    cost: compareYearOverYearTotals(
      toComparableTotal(meter.costs, "cost"),
      priorDetails ? toComparableTotal(priorDetails.costs, "cost") : null
    ),
    usage: compareYearOverYearTotals(
      toComparableTotal(meter.usages, "usage"),
      priorDetails ? toComparableTotal(priorDetails.usages, "usage") : null
    ),
  }
}

const groupBills = (
  bills: readonly SiteBillsChartBill[],
  range: { end: string; start: string } | null
): Map<string, Map<string, MutableMeterMonth>> => {
  const months = new Map<string, Map<string, MutableMeterMonth>>()

  for (const bill of bills) {
    if (
      range != null &&
      !isWithinRange(bill.startDate, range.start, range.end)
    ) {
      continue
    }

    const month = monthKeyForDate(bill.startDate)
    if (!month) {
      continue
    }

    const meterId = String(bill.meterId)
    let monthMeters = months.get(month)
    if (!monthMeters) {
      monthMeters = new Map()
      months.set(month, monthMeters)
    }

    let meter = monthMeters.get(meterId)
    if (!meter) {
      meter = {
        costs: new Map(),
        meterCode: bill.meterCode,
        periods: [],
        usages: new Map(),
      }
      monthMeters.set(meterId, meter)
    }

    meter.periods.push({
      endDate: bill.endDate,
      id: bill.id,
      startDate: bill.startDate,
    })

    const usage = toDecimalAmountString(bill.usage)
    const unit = bill.usageUnit?.trim() ?? ""
    if (usage != null) {
      addAmount(meter.usages, unitKey(unit), unit, usage)
    }

    const cost = toDecimalAmountString(bill.cost)
    const currency = bill.currency?.trim() ?? ""
    if (cost != null && currency) {
      addAmount(meter.costs, currencyKey(currency), currencyKey(currency), cost)
    }
  }

  return months
}

const plottedLabel = (
  meter: SiteBillsMonthMeterDetails,
  measure: SiteBillsChartMeasure
): string | null => amountsForMeasure(meter, measure)[0]?.label ?? null

export const buildSiteBillsMonthlyChart = ({
  bills,
  measure,
  rangeEnd,
  rangeStart,
}: {
  bills: readonly SiteBillsChartBill[]
  measure: SiteBillsChartMeasure
  rangeEnd: string
  rangeStart: string
}): SiteBillsMonthlyChartModel => {
  const months = groupBills(bills, { end: rangeEnd, start: rangeStart })
  const monthsByStart = groupBills(bills, null)
  const plotted = new Map<string, StackedBarGraphSeries>()
  const plottedKeys = new Set<string>()
  let yAxisLabel: string | null = null
  const detailsByMonth: Record<
    string,
    Record<string, SiteBillsMonthMeterDetails>
  > = {}

  const data = monthKeysBetween(rangeStart, rangeEnd).map((month) => {
    const point: StackedBarGraphDataPoint = { xaxis: month }
    const monthMeters = [...(months.get(month)?.entries() ?? [])].map(
      ([meterId, meter]) => {
        const details = toMeterDetails(meterId, meter)

        return {
          ...details,
          yearOverYear: yearOverYearForMeter(month, details, monthsByStart),
        }
      }
    )
    detailsByMonth[month] = Object.fromEntries(
      monthMeters.map((meter) => [meter.meterId, meter])
    )

    if (!monthSharesOneMeasure(monthMeters, measure)) {
      return point
    }

    monthMeters.forEach((meter) => {
      const amount = amountsForMeasure(meter, measure)[0]?.amount
      const label = plottedLabel(meter, measure)
      if (amount == null || label == null) {
        return
      }

      point[meter.meterId] = amount
      plotted.set(meter.meterId, {
        key: meter.meterId,
        label: meter.meterCode,
      })
      const key = normalizeForMeasure(label, measure)
      if (plottedKeys.size === 0) {
        yAxisLabel = label
      }
      plottedKeys.add(key)
      if (plottedKeys.size > 1) {
        yAxisLabel = null
      }
    })

    return point
  })

  return {
    data,
    detailsByMonth,
    series: [...plotted.values()].sort(
      (left, right) =>
        left.label.localeCompare(right.label) ||
        left.key.localeCompare(right.key)
    ),
    yAxisLabel,
  }
}
